import type { AutoCombineEntryV2 } from "@/shared/cache";
import { Badge } from "@/v2/components/ui/badge";
import {
	useSmartTimer,
	useSmartTimerDisplayUnits,
} from "@/v2/hooks/useSmartTimer";

function StaticUnits({ value }: { value: number }) {
	return (
		<div className="flex w-full justify-center">
			<Badge className="min-w-[50px] justify-center rounded-[7px] bg-foreground px-2.5 py-1.5 text-[0.8125rem] font-[525] leading-none tabular-nums text-background">
				{value.toLocaleString("en-US", {
					minimumFractionDigits: 1,
					maximumFractionDigits: 2,
				})}
			</Badge>
		</div>
	);
}

/**
 * The running group's live units. Subscribes to the timer display context in
 * isolation, so only this cell re-renders as time accrues — not the table. Shown
 * as the same `.1` units badge as any other row (updates each billing increment).
 */
function LiveUnits() {
	const units = useSmartTimerDisplayUnits();
	return <StaticUnits value={units} />;
}

/**
 * Units cell for the auto-combine table. Renders a live clock for the group that
 * owns the running (or winding-down) smart timer, and the settled units badge
 * otherwise. Reading the active id here (not per-second state) means every row
 * re-renders only on start/pause, while the tick stays scoped to LiveUnits.
 */
export function RunningUnitsCell({
	entry,
	value,
}: {
	entry: AutoCombineEntryV2;
	value: number;
}) {
	const { activeEntryGroupId, pausingEntryGroupId, pendingTargetId } =
		useSmartTimer();
	const runningId = activeEntryGroupId ?? pausingEntryGroupId;
	// The start response owns the baseline. Until it arrives, retain this row's
	// saved units instead of briefly showing zero or the previous group's clock.
	return runningId === entry.id && pendingTargetId !== entry.id ? (
		<LiveUnits />
	) : (
		<StaticUnits value={value} />
	);
}
